import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { assetUrl } from '@/lib/asset-url';
import { cn } from '@/lib/utils';
import { projectSection, projectTiles, type ProjectTile, type StatAccent } from '@/content/homepage';

const accentText: Record<StatAccent, string> = {
  cyan: 'text-primary',
  coral: 'text-cta',
  violet: 'text-urgency'
};

const accentBar: Record<StatAccent, string> = {
  cyan: 'bg-primary',
  coral: 'bg-cta',
  violet: 'bg-urgency'
};

function InitiativeCard({ project }: { project: ProjectTile }) {
  return (
    <Link
      to={project.href}
      className={cn(
        'group border-border bg-card flex h-full flex-col overflow-hidden rounded-2xl border shadow-[0_18px_48px_-36px_rgb(20_23_31_/_0.45)] transition-all duration-300',
        'hover:-translate-y-1 focus-visible:-translate-y-1',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      )}
    >
      <div className="relative aspect-[16/11] overflow-hidden">
        <img
          src={assetUrl(project.imageSrc)}
          alt={project.imageAlt}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
        <div className={cn('absolute top-0 left-0 h-1 w-full', accentBar[project.accent])} />
      </div>

      <div className="flex flex-1 flex-col gap-4 p-6 sm:p-7">
        <div className="flex flex-wrap items-center gap-2">
          <p className={cn('text-xs font-semibold tracking-[0.18em] uppercase', accentText[project.accent])}>
            {project.indexLabel} · {project.eyebrow}
          </p>
          <Badge
            variant="outline"
            className="border-border bg-muted/40 text-muted-foreground whitespace-normal text-[0.7rem]"
          >
            {project.status}
          </Badge>
        </div>

        <div>
          <h3 className="font-display text-foreground text-2xl font-semibold tracking-tight">
            {project.name}
          </h3>
          <p className="text-foreground mt-2 text-sm font-medium leading-snug sm:text-[0.95rem]">
            {project.descriptor}
          </p>
        </div>

        <p className="text-muted-foreground text-sm leading-relaxed">{project.body}</p>

        <ul className="text-foreground/90 mt-1 space-y-1.5 text-sm">
          {project.bullets.map((bullet) => (
            <li key={bullet} className="flex gap-2.5">
              <span
                className={cn('mt-2 size-1.5 shrink-0 rounded-full', {
                  'bg-primary': project.accent === 'cyan',
                  'bg-cta': project.accent === 'coral',
                  'bg-urgency': project.accent === 'violet'
                })}
              />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>

        <div
          className={cn(
            'mt-auto inline-flex items-center gap-1 pt-3 text-sm font-semibold',
            accentText[project.accent]
          )}
        >
          {project.ctaLabel}
          <ArrowUpRightIcon
            className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            aria-hidden
          />
        </div>
      </div>
    </Link>
  );
}

/**
 * Initiative portfolio — three equal cards (ICONIQ Co-Labs style).
 */
export function ProjectTiles() {
  return (
    <section
      id="what-we-fund"
      aria-labelledby="projects-heading"
      className="scroll-mt-24 px-4 py-20 sm:px-6 sm:py-28 lg:px-8"
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <p className="text-primary mb-4 text-xs font-semibold tracking-[0.24em] uppercase">
            {projectSection.eyebrow}
          </p>
          <h2
            id="projects-heading"
            className="font-display text-foreground text-3xl font-semibold tracking-tight text-balance sm:text-4xl lg:text-[2.75rem]"
          >
            {projectSection.heading}
          </h2>
          <p className="text-muted-foreground mt-5 text-base leading-relaxed sm:text-lg">
            {projectSection.intro}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 xl:gap-8">
          {projectTiles.map((project) => (
            <InitiativeCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
}
